import { createContext, useContext, useEffect, useRef, useState } from "react";
import { DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const ScopeBlocked = createContext(false);
const FormPending = createContext(false);
const MULTILINE = 'textarea, [contenteditable]:not([contenteditable="false"]), [aria-multiline="true"]';
const OWN_KEYS = 'button, a, select, [role="combobox"], [role="listbox"], [role="option"], [role="menu"], [role="menuitem"], [role="switch"], [role="checkbox"], input[type="file"], input[type="checkbox"], input[type="radio"], input[type="range"], input[type="color"]';
const POPUP = '[role="menu"], [role="listbox"], [aria-haspopup][aria-expanded="true"], [role="combobox"][aria-expanded="true"]';

function visible(node) {
  if (!node?.isConnected || node.closest('[hidden], [inert], [aria-hidden="true"], [data-state="closed"]')) return false;
  for (let el = node; el; el = el.parentElement) {
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;
  }
  return true;
}

function activeScope(node) {
  if (!visible(node)) return false;
  const dialogs = [...document.querySelectorAll('[role="dialog"], [role="alertdialog"]')].filter(visible);
  const top = dialogs[dialogs.length - 1];
  if (top && (!top.contains(node) || top.getAttribute("role") === "alertdialog")) return false;
  const scopes = [...document.querySelectorAll('[data-save-scope]')].filter(el => visible(el) && (!top || top.contains(el)));
  return scopes[scopes.length - 1] === node;
}

// A document listener is necessary for body focus, but exists only while this
// scope is mounted and can act only for the visible topmost registered form.
export function SaveScope({ children, blocked = false, ...props }) {
  const ref = useRef(null);
  const latest = useRef(blocked);
  latest.current = blocked;
  useEffect(() => {
    const node = ref.current;
    let composing = false;
    const snapshots = new WeakMap();
    const belongs = target => node.contains(target) || target === node.closest('[role="dialog"]') ||
      target === document.body || target === document.documentElement || target === document;
    const compositionStart = e => { if (belongs(e.target) && activeScope(node)) composing = true; };
    const compositionEnd = () => { composing = false; };
    const capture = e => {
      if (e.key !== "Enter") return;
      // Snapshot before a portaled menu closes during the same key event.
      snapshots.set(e, {
        active: activeScope(node),
        popup: [...document.querySelectorAll(POPUP)].some(visible),
        composing,
      });
    };
    const keydown = e => {
      const snapshot = snapshots.get(e);
      if (e.key !== "Enter" || !snapshot?.active || !activeScope(node) || !belongs(e.target)) return;
      const target = e.target instanceof Element ? e.target : document.activeElement;
      const focused = document.activeElement;
      if (target?.closest(MULTILINE) || focused?.closest(MULTILINE)) return;
      // Keep native keyboard actions (Cancel, Back, dropdowns, file chooser).
      if ((target?.closest(OWN_KEYS) && !target?.closest("[data-form-save]")) ||
          (target === document.body && focused?.closest(OWN_KEYS) && !focused?.closest("[data-form-save]"))) return;
      if (e.defaultPrevented) return;
      const blocked = latest.current || snapshot.popup || snapshot.composing || e.isComposing ||
        e.keyCode === 229 || e.repeat || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey;
      // Prevent an HTML form's implicit submit too; do not interfere with
      // textarea newline or menu-selection events, handled above.
      e.preventDefault();
      if (blocked) return;
      const button = node.querySelector('[data-form-save]');
      if (!button || !visible(button) || button.disabled || button.getAttribute("aria-disabled") === "true") return;
      button.click(); // exact same click / native form validation as Save
    };
    document.addEventListener("keydown", capture, true);
    document.addEventListener("keydown", keydown);
    document.addEventListener("compositionstart", compositionStart, true);
    document.addEventListener("compositionend", compositionEnd, true);
    return () => {
      document.removeEventListener("keydown", capture, true);
      document.removeEventListener("keydown", keydown);
      document.removeEventListener("compositionstart", compositionStart, true);
      document.removeEventListener("compositionend", compositionEnd, true);
    };
  }, []);
  return <ScopeBlocked.Provider value={blocked}><div {...props} ref={ref} data-save-scope="" style={{ display: "contents" }}>{children}</div></ScopeBlocked.Provider>;
}

export function SaveDialogContent({ children, saveBlocked = false, ...props }) {
  return <DialogContent {...props}><SaveScope blocked={saveBlocked}>{children}</SaveScope></DialogContent>;
}

// Re-entrancy guard applies to both mouse and keyboard, including pages whose
// old handlers had no saving state. The existing async save/validation is kept.
export function SaveButton({ onClick, disabled, type = "button", ...props }) {
  const blocked = useContext(ScopeBlocked);
  const formPending = useContext(FormPending);
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const click = async e => {
    if (disabled || blocked || formPending || pending.current) { e.preventDefault(); return; }
    if (!onClick) return; // submit button: let HTML constraint validation run
    pending.current = true;
    setBusy(true);
    try { await onClick(e); }
    finally { pending.current = false; setBusy(false); }
  };
  return <Button {...props} type={type} data-form-save="" disabled={disabled || blocked || formPending || busy} onClick={click} />;
}

export function SaveForm({ onSave, disabled = false, children, ...props }) {
  const blocked = useContext(ScopeBlocked);
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault();
    if (disabled || blocked || pending.current || !e.currentTarget.reportValidity()) return;
    pending.current = true;
    setBusy(true);
    try { await onSave(); }
    finally { pending.current = false; setBusy(false); }
  };
  return <FormPending.Provider value={busy}><form {...props} onSubmit={submit}>{children}</form></FormPending.Provider>;
}
